import { useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { AssetLinks } from '@assets/assets';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { customerPortalMetadata, customerPortalRedirectDetails, useCustomerPortalDocumentsUploadStatus, validateCustomerPortalRedirect } from '@services/customerPortalRedirect';
import { CDX_ACTIONS } from 'types/form';
import { getContactData } from '@services/utils';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { setLocalStorage } from 'utils/setLocalStorage';
import connection from 'utils/signalrService';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

export interface Step {
  stepName: string;
  isCompleted: boolean;
}

const NextStepsPage = () => {
  const { t } = useTexts();
  const { currentStep, widgetInitVariables } = useFormContext();
  const { next } = useScreenNavigator();
  const { trackClick, trackFormSubmit, trackPage, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.NEXT_STEPS);
  const contactData = getContactData();
  const {
    data: customerPortalDocsUploadStatus,
    isValidating: isCustomerPortalDocsUploadStatusLoading,
    mutate,
  } = useCustomerPortalDocumentsUploadStatus(contactData?.contactId, contactData?.leadId);
  
  const { isLoading, dispatch, submitStart, submitSuccess } = useFormContext();
  const [connectionId, setConnectionId] = useState('');
  const [isCustomerPortalValidateLoading, setIsCustomerPortalValidateLoading] = useState(false);

  useEffect(() => {
    if (customerPortalDocsUploadStatus) {
      if ('error' in customerPortalDocsUploadStatus) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: customerPortalDocsUploadStatus,
        });
      } else {
        const isDocumentsUploaded = customerPortalDocsUploadStatus?.isDocumentsUploaded || customerPortalDocsUploadStatus?.isReferenceAdded;
        if (isDocumentsUploaded) {
          //After documents are uploaded and user redirects back navigate to next screen
          setLocalStorage(isDocumentsUploaded, 'documentsUploaded');
          trackFormSubmit('Next Steps Checklist Presented', {
            checklist_items_total: 3,
            checklist_items: ['upload_documents', 'add_references'],
          });
          next(currentStep, 'postSubmitOutcome', { }, true);
        }
        
      }
    }
  }, [customerPortalDocsUploadStatus]);

  useEffect(() => {
    if (!connectionId || connection.state !== 'Disconnected') return;

    const handleSignalRMessage = async (authId: string) => {
      if (!connectionId) {
        console.warn('Missing connection ID');
        return;
      }

      try {
        setIsCustomerPortalValidateLoading(true);
        const response = await validateCustomerPortalRedirect(authId);

        if ('error' in response) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: response,
          });
        } else {
          await mutate();
        }
      } finally {
        // Clean up handler and connection
        connection.off(connectionId, handleSignalRMessage);
        connection.stop();
        setIsCustomerPortalValidateLoading(false);
      }
    };

    connection
      .start()
      .then(() => {
        //SignalR connected
        connection.on(connectionId, handleSignalRMessage);
      })
      //  eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        console.error('SignalR connection failed:', err);
      });
  }, [connectionId, dispatch, mutate]);

  useEffectOnce(() => trackPage(t('pageTitles.NextSteps')));

  const getParam = (key: string) => {
    const parsedUTM = JSON.parse(
      widgetInitVariables?.tracking?.extraData || '{}'
    );
    return parsedUTM?.[key];
  };

  const getTrackingParams = () => {
    return {
      utm_source: getParam('utm_source'),
      utm_medium: getParam('utm_medium'),
      utm_campaign: getParam('utm_campaign'),
      utm_term: getParam('utm_term'),
      utm_content: getParam('utm_content'),
      referrer: getParam('referrer'),
      anonymous_id: getParam('anonymous_id'),
      session_id: getParam('session_id'),
    };
  };

  const handleSubmit = async () => {
    startInteraction();

    trackClick('CTA Clicked', {
      cta_id: 'next_steps_continue',
      cta_text: 'Continue',
      cta_type: 'primary_button',
      cta_context: 'post_submission_checklist',
    });

    const storedContactData = localStorage.getItem('iLendingCDX');

    if (storedContactData) {
      const parsedContactData = JSON.parse(storedContactData);
      const contactData = parsedContactData.contactData ?? null;
      const request = {
        contactId: contactData?.contactId ?? '',
        leadId: contactData?.leadId ?? '',
      };
      submitStart();
      const baseUrl = process.env.REACT_APP_CUSTOMER_PORTAL_URL;
      //open the new tab before async call and then update url to avoid pop up and redirects blocker
      const customerPortalWindow = window.open(baseUrl + '/', '_blank');
      const customerPortalRedirectionResponse = await customerPortalRedirectDetails(request);
      if ('error' in customerPortalRedirectionResponse) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: customerPortalRedirectionResponse,
        });
      } else {
        submitSuccess();
        const customerPortalUrl = new URL(`${baseUrl}/login`);

        const { authId, connectionId } = customerPortalRedirectionResponse;
        setConnectionId(connectionId);

        const requestBody = {
          contactId: contactData?.contactId ?? '',
        };
        const customerPortalMetadataResponse = await customerPortalMetadata(requestBody);
        if ('error' in customerPortalMetadataResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: customerPortalMetadataResponse,
          });
        } else {
          submitSuccess();

          const { metaData } = customerPortalMetadataResponse;
          customerPortalUrl.searchParams.append('metadata', metaData);
          customerPortalUrl.searchParams.append('authId', authId);
          customerPortalUrl.searchParams.append('source', 'CDX');

          // Append utm params to customer portal url
          const trackingParams = getTrackingParams();

          Object.entries(trackingParams).forEach(([key, value]) => {
            if (value) {
              customerPortalUrl.searchParams.append(key, value);
            }
          });
          if (customerPortalWindow) {
            customerPortalWindow.location.href = customerPortalUrl.href;
          }
        }
        
      }
    }
  };

  return (
    <div>
      <div className="iLendingCDX-image-overlay">
        <img
          className="iLendingCDX-ellipse-image"
          src={AssetLinks.ellipse}
          alt="ellipse-image"
        />
        <img
          className="iLendingCDX-confetti-image"
          src={AssetLinks.confetti}
          alt="confetti-image"
        />
      </div>
      <h1 className="iLendingCDX-form-title">{t('nextSteps.title1')}</h1>
      <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-4 ilendingCDX-line-height">{t('nextSteps.title2')}</h2>
      <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-4 ilendingCDX-line-height">{t('nextSteps.title3')}</h2>
      {/* TODO: Uncomment stepper code after Sign Documents is implemented
      <VerticalStepper steps={stepsState} /> */}
      <Button
        type="submit"
        className="iLendingCDX-button--rounded iLendingCDX-next-steps-button"
        size="lg"
        isLoading={isLoading}
        onClick={handleSubmit}
      >
        {t('nextSteps.continue')}
      </Button>
      {(isCustomerPortalDocsUploadStatusLoading || isCustomerPortalValidateLoading) && <OverlaySpinner size="lg" />}
    </div>
  );
};

export default NextStepsPage;
